/**
 * Pure discovery logic: turn a target account's collection list plus the
 * appview map into a row per appview, with a follow verdict for each.
 *
 * No network access here on purpose — every decision is testable.
 */

export const PROFILE_SUFFIXES = ['.profile', '.membership', '.account'];

/** `.declaration` records that declare something other than "this account exists here". */
const NON_PROFILE_DECLARATIONS = new Set([
  'app.bsky.notification.declaration',
  'chat.bsky.actor.declaration',
]);

/** NSID authority is the first two labels: app.rocksky.graph.follow -> app.rocksky */
export function authorityOf(nsid) {
  return nsid.split('.').slice(0, 2).join('.');
}

/**
 * Why this collection looks like a per-appview profile:
 * 'profile' | 'declaration' | 'membership' | 'account', or null.
 */
export function profileKind(nsid) {
  if (PROFILE_SUFFIXES.some((s) => nsid.endsWith(s) && nsid.length > s.length)) {
    return nsid.slice(nsid.lastIndexOf('.') + 1);
  }

  if (nsid.endsWith('.declaration') && !NON_PROFILE_DECLARATIONS.has(nsid)) {
    return 'declaration';
  }

  return null;
}

/** Map an appview-map payload to { authority -> entry }. */
export function buildIndex(appviewMap) {
  const index = new Map();

  for (const appview of appviewMap?.appviews || []) {
    index.set(appview.authority, appview);
  }

  return index;
}

/**
 * Can this tool write the follow record itself? Requires a follow (not
 * subscription) lexicon whose only inputs are the subject DID and a timestamp.
 */
export function followSupport(appview) {
  const follow = appview?.follow;

  if (!follow) {
    return { supported: false, reason: 'No follow record type is known for this appview.' };
  }

  if (follow.kind !== 'follow') {
    return {
      supported: false,
      reason: `The only known record type here is a subscription (${follow.nsid}), which needs more than a follow.`,
    };
  }

  const required = (follow.required && follow.required.length
    ? follow.required
    : Object.keys(follow.sample || {})
  ).filter((p) => p !== '$type');

  if (!required.includes('subject')) {
    return { supported: false, reason: `${follow.nsid} has no subject field, so it cannot target an account.` };
  }

  const unfillable = required.filter((p) => p !== 'subject' && p !== 'createdAt');

  if (unfillable.length) {
    return {
      supported: false,
      reason: `${follow.nsid} also requires ${unfillable.join(', ')}, which this tool cannot fill.`,
    };
  }

  return { supported: true, reason: null };
}

const STATE_ORDER = [
  'following',
  'available',
  'subscription',
  'unsupported-lexicon',
  'no-lexicon',
  'unknown-appview',
];

/**
 * @param {object} args
 * @param {string[]} args.collections  target account's collections
 * @param {Map<string, object>} args.index  authority -> appview map entry
 * @param {{[nsid: string]: {[subjectDid: string]: {uri: string, rkey: string}}}} args.followedByNsid
 *   follow nsid -> subject DID -> the record we already hold
 * @param {string} args.targetDid
 * @param {boolean} args.includeActive  keep appviews with records but no profile record
 */
export function planAppviews({
  collections = [],
  index,
  followedByNsid = {},
  targetDid,
  includeActive = true,
}) {
  const byAuthority = new Map();

  for (const nsid of collections) {
    const authority = authorityOf(nsid);

    if (!byAuthority.has(authority)) byAuthority.set(authority, []);
    byAuthority.get(authority).push(nsid);
  }

  const rows = [];

  for (const [authority, nsids] of byAuthority) {
    const profiles = nsids
      .map((nsid) => ({ nsid, kind: profileKind(nsid) }))
      .filter((p) => p.kind)
      .sort((a, b) => (a.kind === 'profile' ? -1 : 0) - (b.kind === 'profile' ? -1 : 0));

    if (!profiles.length && !includeActive) continue;

    const entry = index.get(authority) || null;
    const support = followSupport(entry);
    const followNsid = entry?.follow?.nsid || null;

    const existing = targetDid ? followedByNsid[followNsid]?.[targetDid] || null : null;
    const isFollowing = Boolean(existing);

    let state;

    if (support.supported) state = isFollowing ? 'following' : 'available';
    else if (entry?.follow?.kind === 'subscription') state = 'subscription';
    else if (entry?.follow) state = 'unsupported-lexicon';
    else if (entry) state = 'no-lexicon';
    else state = 'unknown-appview';

    rows.push({
      authority,
      label: entry?.authority || authority,
      profile: profiles[0] || null,
      profiles,
      collections: nsids.sort(),
      followNsid,
      followKind: entry?.follow?.kind || null,
      supported: support.supported,
      blockedReason: support.reason,
      state,
      isFollowing,
      existing,
    });
  }

  return rows.sort((a, b) => {
    const byState = STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state);

    return byState !== 0 ? byState : a.authority.localeCompare(b.authority);
  });
}

export function summarise(rows) {
  const counts = {
    appviews: rows.length,
    withProfile: rows.filter((r) => r.profile).length,
    following: rows.filter((r) => r.state === 'following').length,
    available: rows.filter((r) => r.state === 'available').length,
    followable: rows.filter((r) => r.supported).length,
  };

  return counts;
}

/** The record written into the signed-in account's repo to follow someone. */
export function buildFollowRecord({ nsid, subjectDid, now = new Date().toISOString() }) {
  return {
    $type: nsid,
    subject: subjectDid,
    createdAt: now,
  };
}

/** Appviews where a follow write is both possible and not already done. */
export function pendingFollows(rows) {
  return rows.filter((row) => row.supported && !row.isFollowing);
}
